import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { journeyApi } from '../../api/journeyApi';
import useMemberDisplayNames from '../member-display/useMemberDisplayNames';
import { memberDisplayNameWithId } from '../member-display/memberDisplayModel';
import {
  canMutateIncomingInvitation,
  invitationErrorMessage,
  isPositiveId,
  normalizeInvitation,
  normalizeInvitationInboxPage,
  partitionInvitationInbox,
} from './journeyInvitationModel';
import '../my-journeys/my-journeys.css';
import './journey-invitation.css';

const PAGE_LIMIT = 20;

function InvitationCard({
  invitation,
  inviterLabel,
  highlighted,
  pendingAction,
  confirmDeclineId,
  onAccept,
  onDecline,
}) {
  const mutating = Boolean(pendingAction);
  const canMutate = canMutateIncomingInvitation(invitation);
  return (
    <li>
      <article
        className={`journey-invite-card${highlighted ? ' is-focused' : ''}`}
        aria-labelledby={`invitation-title-${invitation.invitationId}`}
      >
        <p className="journey-invite-card__meta">
          <span>{invitation.statusLabel}</span>
          {invitation.intendedRoleLabel ? ` · ${invitation.intendedRoleLabel}` : ''}
        </p>
        <h3 id={`invitation-title-${invitation.invitationId}`}>
          {invitation.journeyTitle || `旅程 ${invitation.journeyId}`}
        </h3>
        {inviterLabel ? <p>邀請人 {inviterLabel}</p> : null}
        {invitation.invitedAt ? <p>邀請時間 {invitation.invitedAt}</p> : null}
        {invitation.expiresAt ? <p>到期 {invitation.expiresAt}</p> : null}
        {canMutate ? (
          <div className="journey-invitations__actions">
            <button
              type="button"
              className="journey-workspace__lifecycle-primary"
              disabled={mutating}
              aria-label={`接受邀請：${invitation.journeyTitle || `旅程 ${invitation.journeyId}`}`}
              onClick={() => onAccept(invitation)}
            >
              {pendingAction === 'accept' ? '接受中…' : '接受邀請'}
            </button>
            <button
              type="button"
              className="journey-workspace__lifecycle-danger"
              disabled={mutating}
              aria-label={confirmDeclineId && confirmDeclineId === invitation.invitationId
                ? `確認拒絕邀請：${invitation.journeyTitle || `旅程 ${invitation.journeyId}`}`
                : `拒絕邀請：${invitation.journeyTitle || `旅程 ${invitation.journeyId}`}`}
              onClick={() => onDecline(invitation)}
            >
              {pendingAction === 'decline'
                ? '拒絕中…'
                : confirmDeclineId && confirmDeclineId === invitation.invitationId
                  ? '確認拒絕邀請'
                  : '拒絕邀請'}
            </button>
          </div>
        ) : invitation.status === 'ACCEPTED' && isPositiveId(invitation.journeyId) ? (
          <div className="journey-invitations__actions">
            <Link className="journey-invite-card__enter" to={`/journeys/${invitation.journeyId}`}>進入旅程</Link>
          </div>
        ) : null}
      </article>
    </li>
  );
}

export default function JourneyInvitationsPage() {
  const invitationFieldId = useId();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const busy = useRef(false);
  const loadMoreBusy = useRef(false);
  const initialId = searchParams.get('invitationId') || '';
  const [invitationId, setInvitationId] = useState(initialId);
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [phase, setPhase] = useState('loading');
  const [listError, setListError] = useState('');
  const [pendingAction, setPendingAction] = useState('');
  const [pendingInvitationId, setPendingInvitationId] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [confirmDeclineId, setConfirmDeclineId] = useState('');
  const [focusedId, setFocusedId] = useState(isPositiveId(initialId) ? initialId.trim() : '');

  const locked = Boolean(pendingAction);
  const actionable = isPositiveId(invitationId);
  const inviterSummaries = useMemberDisplayNames(items.map((item) => item.inviterMemberId));
  const { pending, history } = partitionInvitationInbox(items);

  const loadInbox = useCallback(async ({ cursor, append } = {}) => {
    const page = normalizeInvitationInboxPage(await journeyApi.listIncomingInvitations({
      limit: PAGE_LIMIT,
      ...(cursor || {}),
    }));
    setItems((current) => {
      if (!append) return page.items;
      const seen = new Set(current.map((item) => item.invitationId));
      return [...current, ...page.items.filter((item) => !seen.has(item.invitationId))];
    });
    setNextCursor(page.nextCursor);
    setHasMore(page.hasMore);
    return page;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPhase('loading');
      setListError('');
      try {
        const page = await loadInbox();
        if (cancelled) return;
        const deepLink = isPositiveId(initialId) ? initialId.trim() : '';
        if (deepLink && !page.items.some((item) => item.invitationId === deepLink)) {
          try {
            const detail = normalizeInvitation(await journeyApi.getIncomingInvitation(deepLink));
            if (!cancelled && detail.invitationId) {
              setItems((current) => (
                current.some((item) => item.invitationId === detail.invitationId)
                  ? current
                  : [detail, ...current]
              ));
            }
          } catch {
            /* keep manual fallback */
          }
        }
        setPhase('ready');
      } catch (cause) {
        if (cancelled) return;
        setListError(invitationErrorMessage(cause));
        setPhase('error');
      }
    })();
    return () => { cancelled = true; };
  }, [initialId, loadInbox]);

  async function refreshInbox() {
    await loadInbox();
  }

  async function run(action, targetId = invitationId) {
    const id = String(targetId || '').trim();
    if (!isPositiveId(id) || busy.current) return;
    if (action === 'decline' && confirmDeclineId !== id) {
      setConfirmDeclineId(id);
      return;
    }
    busy.current = true;
    setPendingAction(action);
    setPendingInvitationId(id);
    setError('');
    try {
      const payload = action === 'accept'
        ? await journeyApi.acceptInvitation(id)
        : await journeyApi.declineInvitation(id);
      const normalized = normalizeInvitation(payload);
      setResult({ action, invitation: normalized });
      setConfirmDeclineId('');
      await refreshInbox();
      if (action === 'accept' && isPositiveId(normalized.journeyId)) {
        navigate(`/journeys/${normalized.journeyId}`, { replace: false });
        return;
      }
    } catch (cause) {
      setError(invitationErrorMessage(cause));
      try { await refreshInbox(); } catch { /* keep mutation error */ }
    } finally {
      busy.current = false;
      setPendingAction('');
      setPendingInvitationId('');
    }
  }

  async function loadMore() {
    if (!hasMore || !nextCursor || loadMoreBusy.current) return;
    loadMoreBusy.current = true;
    try {
      await loadInbox({ cursor: nextCursor, append: true });
    } catch (cause) {
      setError(invitationErrorMessage(cause));
    } finally {
      loadMoreBusy.current = false;
    }
  }

  return (
    <main className="journey-invitations">
      <header className="journey-invitations__header">
        <p className="my-journeys__eyebrow">旅程工作區</p>
        <h1>旅程邀請</h1>
        <p>這裡列出你收到的旅程邀請。接受後才會加入旅程。</p>
        <Link to="/journeys">返回我的行程</Link>
      </header>

      {phase === 'loading' ? <p aria-live="polite">載入邀請…</p> : null}
      {phase === 'error' ? <p className="journey-workspace__error" role="alert">{listError}</p> : null}

      {phase === 'ready' && pending.length === 0 && history.length === 0 ? (
        <p aria-live="polite">目前沒有旅程邀請。</p>
      ) : null}

      {pending.length ? (
        <section aria-labelledby="pending-invites-heading">
          <h2 id="pending-invites-heading">待處理</h2>
          <p className="sr-only" aria-live="polite">{pending.length} 張待回覆邀請</p>
          <ul className="journey-invite-list">
            {pending.map((invitation) => (
              <InvitationCard
                key={invitation.invitationId}
                invitation={invitation}
                inviterLabel={invitation.inviterMemberId
                  ? memberDisplayNameWithId(invitation.inviterMemberId, inviterSummaries)
                  : ''}
                highlighted={focusedId === invitation.invitationId}
                pendingAction={pendingInvitationId === invitation.invitationId ? pendingAction : ''}
                confirmDeclineId={confirmDeclineId}
                onAccept={(item) => run('accept', item.invitationId)}
                onDecline={(item) => run('decline', item.invitationId)}
              />
            ))}
          </ul>
        </section>
      ) : null}

      {history.length ? (
        <section aria-labelledby="history-invites-heading">
          <h2 id="history-invites-heading">歷史</h2>
          <ul className="journey-invite-list">
            {history.map((invitation) => (
              <InvitationCard
                key={invitation.invitationId}
                invitation={invitation}
                inviterLabel={invitation.inviterMemberId
                  ? memberDisplayNameWithId(invitation.inviterMemberId, inviterSummaries)
                  : ''}
                highlighted={focusedId === invitation.invitationId}
                pendingAction={pendingInvitationId === invitation.invitationId ? pendingAction : ''}
                confirmDeclineId={confirmDeclineId}
                onAccept={(item) => run('accept', item.invitationId)}
                onDecline={(item) => run('decline', item.invitationId)}
              />
            ))}
          </ul>
        </section>
      ) : null}

      {hasMore ? (
        <button type="button" className="journey-invitations__more" onClick={loadMore}>
          載入較早邀請
        </button>
      ) : null}

      <section className="journey-invitations__fallback" aria-labelledby="manual-invite-heading">
        <h2 id="manual-invite-heading">使用邀請代碼</h2>
        <p>若你有邀請代碼，仍可直接接受或拒絕。</p>
        <form
          className="journey-invitations__form"
          onSubmit={(event) => {
            event.preventDefault();
            run('accept');
          }}
          noValidate
        >
          <label htmlFor={invitationFieldId}>
            邀請代碼
            <input
              id={invitationFieldId}
              inputMode="numeric"
              autoComplete="off"
              value={invitationId}
              disabled={locked}
              onChange={(event) => {
                setInvitationId(event.target.value);
                setConfirmDeclineId('');
                setResult(null);
                setFocusedId(event.target.value.trim());
              }}
            />
          </label>
          <div className="journey-invitations__actions">
            <button
              type="submit"
              className="journey-workspace__lifecycle-primary"
              disabled={!actionable || locked}
            >
              {pendingAction === 'accept' && pendingInvitationId === invitationId.trim() ? '接受中…' : '接受邀請'}
            </button>
            <button
              type="button"
              className="journey-workspace__lifecycle-danger"
              disabled={!actionable || locked}
              onClick={() => run('decline')}
            >
              {pendingAction === 'decline' && pendingInvitationId === invitationId.trim()
                ? '拒絕中…'
                : confirmDeclineId && confirmDeclineId === invitationId.trim()
                  ? '確認拒絕邀請'
                  : '拒絕邀請'}
            </button>
          </div>
        </form>
      </section>

      {error ? <p className="journey-workspace__error" role="alert">{error}</p> : null}
      {result?.action === 'decline' ? (
        <p className="journey-invite__success" role="status">
          已拒絕旅程邀請
          {result.invitation.status ? `（${result.invitation.status}）` : ''}。尚未加入旅程。
        </p>
      ) : null}
    </main>
  );
}
